// Thumbnail rendering on a 1280x720 canvas: video frame + static Voice Bars + Impact caps text.
export const THUMB_W = 1280, THUMB_H = 720;
export const TEXT_FONT = 'Impact, "Anton", "Arial Narrow", sans-serif';
export const FRAME_LABELS = ["Cover", "25%", "50%", "75%"];
export const DEFAULT_THUMB = {
  frame: 0, text: "",
  text_box: { x: 0.04, y: 0.55, w: 0.62, h: 0.38 },
  bars_box: { x: 0.74, y: 0.08, w: 0.22 },
  shapes: [], highlight: null,
};
export const HIGHLIGHT_COLORS = { yellow: "#facc15", red: "#ef4444" };
const SHAPE_RED = "#ef4444";
const newId = () => Math.random().toString(36).slice(2, 10);
export const newArrow = () => ({ id: newId(), type: "arrow", x: 0.35, y: 0.3, x2: 0.5, y2: 0.45 });
export const newCircle = () => ({ id: newId(), type: "circle", x: 0.4, y: 0.3, w: 0.18, h: 0.3 });
export const thumbWords = (text) => text.toUpperCase().split(/\s+/).filter(Boolean);

export const framePreviewUrl = (videoId, n) => `https://i.ytimg.com/vi/${videoId}/${n === 0 ? "hqdefault" : `hq${n}`}.jpg`;

const loadImg = (src) => new Promise((res, rej) => {
  const img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = () => (img.naturalWidth > 120 ? res(img) : rej(new Error("placeholder")));
  img.onerror = rej;
  img.src = src;
});

// Best available resolution for frame n (0 = cover, 1-3 = YouTube's auto stills).
export function loadFrame(videoId, n) {
  const names = n === 0 ? ["maxresdefault", "sddefault", "hqdefault"] : [`maxres${n}`, `sd${n}`, `hq${n}`];
  return names.reduce((p, name) => p.catch(() => loadImg(`https://i.ytimg.com/vi/${videoId}/${name}.jpg`)), Promise.reject());
}

export const loadThumbFont = () => document.fonts?.load(`80px Anton`).catch(() => {});

const LINE = 1.02;
const wrap = (g, words, maxW, size) => {
  g.font = `${size}px ${TEXT_FONT}`;
  const lines = [];
  for (const w of words) {
    const last = lines[lines.length - 1];
    if (last && g.measureText(`${last} ${w}`).width <= maxW) lines[lines.length - 1] = `${last} ${w}`;
    else lines.push(w);
  }
  return lines;
};

// Largest font size whose wrapped lines fill the box without overflowing it.
export function fitText(g, text, w, h) {
  const words = text.toUpperCase().split(/\s+/).filter(Boolean);
  if (!words.length) return { size: 0, lines: [] };
  let lo = 6, hi = h, best = { size: lo, lines: wrap(g, words, w, lo) };
  while (hi - lo > 0.5) {
    const mid = (lo + hi) / 2;
    const lines = wrap(g, words, w, mid);
    if (lines.length * mid * LINE <= h && lines.every((l) => g.measureText(l).width <= w)) { best = { size: mid, lines }; lo = mid; } else hi = mid;
  }
  return best;
}

function drawText(g, text, box, highlight) {
  const bw = box.w * THUMB_W, bh = box.h * THUMB_H;
  const { size, lines } = fitText(g, text, bw * 0.94, bh * 0.9);
  if (!lines.length) return;
  g.font = `${size}px ${TEXT_FONT}`;
  g.textAlign = "left"; g.textBaseline = "middle";
  g.lineJoin = "round"; g.lineWidth = size * 0.14; g.strokeStyle = "#000";
  const cx = box.x * THUMB_W + bw / 2, top = box.y * THUMB_H + (bh - lines.length * size * LINE) / 2;
  const words = [];
  lines.forEach((l, i) => {
    const y = top + (i + 0.5) * size * LINE;
    let x = cx - g.measureText(l).width / 2;
    l.split(" ").forEach((w) => { words.push({ w, x, y }); x += g.measureText(`${w} `).width; });
  });
  words.forEach(({ w, x, y }) => g.strokeText(w, x, y));
  words.forEach(({ w, x, y }, i) => {
    g.fillStyle = highlight && highlight.index === i ? HIGHLIGHT_COLORS[highlight.color] : "#fff";
    g.fillText(w, x, y);
  });
}

function drawShape(g, s) {
  g.save();
  g.lineJoin = "round";
  if (s.type === "circle") {
    const cx = (s.x + s.w / 2) * THUMB_W, cy = (s.y + s.h / 2) * THUMB_H, rx = (s.w / 2) * THUMB_W, ry = (s.h / 2) * THUMB_H;
    const t = Math.max(8, Math.min(rx, ry) * 0.12);
    g.beginPath(); g.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
    g.strokeStyle = "#000"; g.lineWidth = t + 8; g.stroke();
    g.strokeStyle = SHAPE_RED; g.lineWidth = t; g.stroke();
  } else {
    const x1 = s.x * THUMB_W, y1 = s.y * THUMB_H, x2 = s.x2 * THUMB_W, y2 = s.y2 * THUMB_H;
    const len = Math.hypot(x2 - x1, y2 - y1);
    if (len < 4) { g.restore(); return; }
    const t = Math.min(Math.max(len * 0.09, 10), 34), head = Math.min(t * 3, len * 0.6);
    g.translate(x1, y1); g.rotate(Math.atan2(y2 - y1, x2 - x1));
    g.beginPath();
    g.moveTo(0, -t / 2); g.lineTo(len - head, -t / 2); g.lineTo(len - head, -head * 0.55);
    g.lineTo(len, 0); g.lineTo(len - head, head * 0.55); g.lineTo(len - head, t / 2); g.lineTo(0, t / 2); g.closePath();
    g.strokeStyle = "#000"; g.lineWidth = 8; g.stroke();
    g.fillStyle = SHAPE_RED; g.fill();
  }
  g.restore();
}

const LEVELS = [0.4, 0.6, 0.85, 0.7, 1, 0.75, 0.55, 0.9, 1, 0.8, 0.6, 0.95, 0.6, 0.45];
const rowColor = (c) => (c < 2 ? "239,68,68" : c < 5 ? "156,163,175" : "255,255,255");

// A frozen, "speaking" snapshot of the animated Voice Bars box.
function drawBars(g, box, label) {
  const x0 = box.x * THUMB_W, y0 = box.y * THUMB_H, s = box.w * THUMB_W;
  g.save();
  g.fillStyle = "#000";
  g.beginPath(); g.roundRect(x0, y0, s, s, s * 0.03); g.fill();
  const BARS = 14, CELLS = 7, h = s * 0.83;
  const floor = h * 0.76, pad = s * 0.1, topPad = h * 0.1, gap = s * 0.014;
  const bw = (s - pad * 2 - gap * (BARS - 1)) / BARS, ch = (floor - topPad - gap * CELLS) / CELLS;
  for (let i = 0; i < BARS; i++) {
    const lit = Math.max(1, Math.round(LEVELS[i] * CELLS)), x = x0 + pad + i * (bw + gap);
    for (let c = 0; c < CELLS; c++) {
      const on = c < lit, col = rowColor(c);
      g.shadowColor = `rgba(${col},0.9)`; g.shadowBlur = on ? s * 0.03 : 0;
      g.fillStyle = `rgba(${col},${on ? 1 : 0.08})`;
      g.fillRect(x, y0 + floor - gap - (c + 1) * ch - c * gap, bw, ch);
    }
    g.shadowBlur = 0;
    for (let c = 0; c < Math.min(lit, 2); c++) {
      g.fillStyle = `rgba(${rowColor(c)},${0.3 * (1 - c / 2)})`;
      g.fillRect(x, y0 + floor + c * (ch + gap), bw, ch);
    }
  }
  let fs = s * 0.065;
  const ly = y0 + h + (s - h) * 0.42;
  g.font = `400 ${fs}px Outfit, sans-serif`;
  const fit = (s * 0.9) / (g.measureText(label).width + fs * 1.6);
  if (fit < 1) { fs *= fit; g.font = `400 ${fs}px Outfit, sans-serif`; }
  g.textBaseline = "middle"; g.textAlign = "left"; g.fillStyle = "#fff";
  const tw = g.measureText(label).width, dot = fs * 1.1, lx = x0 + (s - (dot + fs * 0.5 + tw)) / 2;
  g.beginPath(); g.arc(lx + dot / 2, ly, dot / 2, 0, Math.PI * 2); g.fill();
  g.fillText(label, lx + dot + fs * 0.5, ly);
  g.strokeStyle = "#facc15"; g.lineWidth = Math.max(2, s * 0.012);
  g.beginPath(); g.roundRect(x0, y0, s, s, s * 0.03); g.stroke();
  g.restore();
}

export function renderThumbnail(canvas, img, thumb, label = "Reaction") {
  canvas.width = THUMB_W; canvas.height = THUMB_H;
  const g = canvas.getContext("2d");
  g.fillStyle = "#000"; g.fillRect(0, 0, THUMB_W, THUMB_H);
  if (img) {
    const sw = img.naturalWidth, sh = Math.min(img.naturalHeight, sw * 9 / 16);
    g.drawImage(img, 0, (img.naturalHeight - sh) / 2, sw, sh, 0, 0, THUMB_W, THUMB_H);
  }
  drawBars(g, thumb.bars_box, label);
  (thumb.shapes || []).forEach((s) => drawShape(g, s));
  drawText(g, thumb.text, thumb.text_box, thumb.highlight);
}

export function downloadCanvas(canvas, name = "thumbnail.jpg") {
  canvas.toBlob((blob) => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = name;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  }, "image/jpeg", 0.92);
}
